import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { TrackDataset } from "../format";

const TRAIL_LEN = 14;

function circleTexture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas unavailable");
  const g = ctx.createRadialGradient(size / 2, size / 2, 2, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.45, "rgba(255,255,255,0.85)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

export class TrackScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly camera: THREE.PerspectiveCamera;
  readonly scene: THREE.Scene;
  readonly controls: OrbitControls;
  private readonly root: THREE.Group;
  private points: THREE.Points | null = null;
  private trails: THREE.LineSegments | null = null;
  private videoMesh: THREE.Mesh | null = null;
  private dataset: TrackDataset | null = null;
  private frame = 0;
  private showTrails = true;
  private showVideo = true;
  private running = false;
  private readonly pointTex: THREE.Texture;
  private readonly planeSize = { w: 2.4, h: 1.35 };

  constructor(private readonly host: HTMLElement) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x07090d);
    this.scene.fog = new THREE.Fog(0x07090d, 6, 14);

    this.camera = new THREE.PerspectiveCamera(45, 1, 0.05, 40);
    this.camera.position.set(0.15, 0.2, 3.35);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    host.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.target.set(0, 0, 0.2);
    this.controls.minDistance = 1.1;
    this.controls.maxDistance = 7;
    this.controls.maxPolarAngle = Math.PI / 1.85;

    const ambient = new THREE.AmbientLight(0xb9c6ff, 0.55);
    const key = new THREE.DirectionalLight(0xe8f1ff, 0.8);
    key.position.set(2, 3, 4);
    this.scene.add(ambient, key);

    const grid = new THREE.GridHelper(8, 16, 0x4a6a88, 0x243140);
    grid.position.y = -1.05;
    this.scene.add(grid);
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(8, 8),
      new THREE.MeshBasicMaterial({ color: 0x0c1218, side: THREE.DoubleSide }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.051;
    this.scene.add(floor);

    this.root = new THREE.Group();
    this.scene.add(this.root);
    this.pointTex = circleTexture();

    const axes = new THREE.AxesHelper(0.35);
    axes.position.set(-1.35, -0.85, 0);
    this.scene.add(axes);

    this.running = true;
    this.resize();
    requestAnimationFrame(() => this.resize());
    this.loop();
  }

  setDataset(dataset: TrackDataset, video: HTMLVideoElement): void {
    this.dataset = dataset;
    this.clearCloud();
    if (dataset.tracks.length === 0 || dataset.frameCount === 0) return;

    const aspect = dataset.width / Math.max(1, dataset.height);
    this.planeSize.w = 2.4;
    this.planeSize.h = this.planeSize.w / aspect;

    const tex = new THREE.VideoTexture(video);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(this.planeSize.w, this.planeSize.h),
      new THREE.MeshBasicMaterial({ map: tex, toneMapped: false }),
    );
    plane.position.z = -0.02;
    this.videoMesh = plane;
    this.root.add(plane);

    const n = dataset.tracks.length;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    geo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    const mat = new THREE.PointsMaterial({
      size: 0.055,
      map: this.pointTex,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
      alphaTest: 0.12,
    });
    this.points = new THREE.Points(geo, mat);
    this.root.add(this.points);
    this.camera.position.set(0.2, 0.28, 3.15);
    this.controls.target.set(0, 0, 0.22);
    this.controls.update();

    const trailGeo = new THREE.BufferGeometry();
    const maxSeg = n * (TRAIL_LEN - 1);
    trailGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(maxSeg * 2 * 3), 3));
    trailGeo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(maxSeg * 2 * 3), 3));
    const trailMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.trails = new THREE.LineSegments(trailGeo, trailMat);
    this.root.add(this.trails);

    this.setFrame(0);
  }

  setFrame(frame: number): void {
    const dataset = this.dataset;
    if (!dataset || !this.points) return;
    this.frame = Math.max(0, Math.min(dataset.frameCount - 1, frame));
    const pos = this.points.geometry.getAttribute("position") as THREE.BufferAttribute;
    const col = this.points.geometry.getAttribute("color") as THREE.BufferAttribute;
    const { w, h } = this.planeSize;

    for (let i = 0; i < dataset.tracks.length; i++) {
      const track = dataset.tracks[i];
      const p = track.points[this.frame];
      if (!p || p.vis < 0.5) {
        pos.setXYZ(i, 0, -20, 0);
        col.setXYZ(i, 0, 0, 0);
        continue;
      }
      const x = (p.u / dataset.width - 0.5) * w;
      const y = (0.5 - p.v / dataset.height) * h;
      const z = 0.04 + track.dynamic * 0.55;
      pos.setXYZ(i, x, y, z);
      const warm = track.dynamic;
      col.setXYZ(
        i,
        track.color[0] * (1 - 0.35 * warm) + 1.0 * warm,
        track.color[1] * (1 - 0.25 * warm) + 0.45 * warm,
        track.color[2] * (1 - 0.55 * warm) + 0.18 * warm,
      );
    }
    pos.needsUpdate = true;
    col.needsUpdate = true;

    if (this.trails) {
      this.trails.visible = this.showTrails;
      if (this.showTrails) this.writeTrails();
    }
    if (this.videoMesh) this.videoMesh.visible = this.showVideo;
  }

  setShowTrails(on: boolean): void {
    this.showTrails = on;
    if (this.trails) this.trails.visible = on;
    this.setFrame(this.frame);
  }

  setShowVideo(on: boolean): void {
    this.showVideo = on;
    if (this.videoMesh) this.videoMesh.visible = on;
  }

  get currentFrame(): number {
    return this.frame;
  }

  resize(): void {
    const { clientWidth, clientHeight } = this.host;
    const w = Math.max(1, clientWidth);
    const h = Math.max(1, clientHeight);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
  }

  dispose(): void {
    this.running = false;
    this.controls.dispose();
    this.renderer.dispose();
    this.pointTex.dispose();
    this.renderer.domElement.remove();
  }

  private writeTrails(): void {
    if (!this.dataset || !this.trails) return;
    const dataset = this.dataset;
    const pos = this.trails.geometry.getAttribute("position") as THREE.BufferAttribute;
    const col = this.trails.geometry.getAttribute("color") as THREE.BufferAttribute;
    const { w, h } = this.planeSize;
    let cursor = 0;
    const max = pos.count;

    const project = (u: number, v: number, dynamic: number): [number, number, number] => {
      const x = (u / dataset.width - 0.5) * w;
      const y = (0.5 - v / dataset.height) * h;
      const z = 0.04 + dynamic * 0.55;
      return [x, y, z];
    };

    for (const track of dataset.tracks) {
      if (track.dynamic < 0.18) continue;
      let prev: [number, number, number] | null = null;
      const start = Math.max(0, this.frame - TRAIL_LEN + 1);
      for (let t = start; t <= this.frame; t++) {
        const p = track.points[t];
        if (!p || p.vis < 0.5) {
          prev = null;
          continue;
        }
        const cur = project(p.u, p.v, track.dynamic);
        if (prev && cursor + 1 < max) {
          const fade = (t - start) / Math.max(1, this.frame - start);
          pos.setXYZ(cursor, prev[0], prev[1], prev[2]);
          pos.setXYZ(cursor + 1, cur[0], cur[1], cur[2]);
          const r = track.color[0] * 0.4 + 0.9 * fade;
          const g = track.color[1] * 0.4 + 0.35 * fade;
          const b = track.color[2] * 0.5 + 0.15 * fade;
          col.setXYZ(cursor, r, g, b);
          col.setXYZ(cursor + 1, r, g, b);
          cursor += 2;
        }
        prev = cur;
      }
    }
    for (let i = cursor; i < max; i++) {
      pos.setXYZ(i, 0, -20, 0);
      col.setXYZ(i, 0, 0, 0);
    }
    pos.needsUpdate = true;
    col.needsUpdate = true;
  }

  private clearCloud(): void {
    for (const child of [...this.root.children]) {
      child.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat?.dispose();
      });
      this.root.remove(child);
    }
    this.points = null;
    this.trails = null;
    this.videoMesh = null;
  }

  private loop = (): void => {
    if (!this.running) return;
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(this.loop);
  };
}
